/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import React, {PureComponent, MouseEventHandler} from 'react';
import {HTMLVideoEvent, MediaType} from '../util/interfaces';
import {generateComponentId} from "../util/componentUtil";

interface Props {
    onVideoEnd?: VoidFunction;
    onClick?: MouseEventHandler;
    onCanPlay?: HTMLVideoEvent;
    filename: string;
    source?: string;
    className?: string;
    imgAlt?: string;
    mediaType?: MediaType;
    controls?: boolean;
    loop?: boolean;
    autoPlay?: boolean;
    muted?: boolean;
    extension?: string;
    id?: string;
}

const videoExtensions = ['.mp4', '.webm'];

export default class Media extends PureComponent<Props> {
    private readonly id = generateComponentId(this.props.id);

    getExtension = (): string => {
        const {source, filename, extension} = this.props;
        if (extension) return extension;
        if (source && source.match(/\.[A-Za-z\d]+$/))
            return source.substr(source.lastIndexOf('.'), source.length);
        else if (filename.match(/\.[A-Za-z\d]+$/))
            return filename.substr(filename.lastIndexOf('.'), filename.length);
        throw new TypeError('No extension found in either the source or filename')
    };

    getMediaType = (): MediaType => {
        const {mediaType} = this.props;
        if (mediaType) return mediaType;
        const extension: string = this.getExtension();
        return videoExtensions.includes(extension) ? MediaType.VIDEO : MediaType.IMAGE;
    };

    renderImgElement = () => {
        const {source, filename, imgAlt, onClick, className} = this.props;
        return (
            <img id={this.id} src={source ?? filename} alt={imgAlt ?? 'DVR Controls'} onClick={onClick} className={className}/>
        );
    };

    renderVideoElement = () => {
        const {source, filename, onClick, onVideoEnd, className, controls, autoPlay, loop, muted, onCanPlay} = this.props,
            extension = this.getExtension();
        return (
            <video onEnded={onVideoEnd} className={className} controls={controls} autoPlay={autoPlay} loop={loop}
                   muted={muted} onClick={onClick} id={this.id} onCanPlay={onCanPlay}>
                <source src={source ?? filename} type={`video/${extension.substr(1)}`}/>
                This browser does not support the HTML5 video element.
            </video>
        )
    };

    render = () => {
        const mediaType: MediaType = this.getMediaType();
        if (mediaType === MediaType.IMAGE) return this.renderImgElement();
        return this.renderVideoElement();
    }
}